/* ─── Brand (mirrors app-wide tokens) ─────────────────────────── */
export const B = {
    deep:    '#0D0820',
    dark:    '#1A0F3D',
    mid:     '#2D1B69',
    purple:  '#7B5EA7',
    light:   '#9B7EC8',
    accent:  '#C8B8E8',
    lavender:'#F7F4FC',
    ink:     '#0D0A1A',
    green:   '#00C896',
    greenDim:'#00C89618',
    red:     '#FF4D6A',
    redDim:  '#FF4D6A18',
    gold:    '#F5C842',
    blue:    '#007AFF',
    orange:  '#FF9500',
};

/* ─── Types ────────────────────────────────────────────────────── */
export interface MutualFund {
    id: string;
    name: string;
    manager: string;
    category: string;
    risk: 'Low' | 'Low-Medium' | 'Medium' | 'High';
    riskColor: string;
    returns: { d1: number; ytd: number; yr1: number; yr3: number };
    minInvestment: number;
    nav: number;
    aum: string;
    color: string;
    description: string;
    highlights: string[];
    chart: number[];
}

export interface Stock {
    id: string;
    ticker: string;
    name: string;
    sector: string;
    price: number;
    change: number;
    changeAbs: number;
    high52: number;
    low52: number;
    marketCap: string;
    peRatio: number;
    dividend: number;
    color: string;
    description: string;
    chart: number[];
}

/* ─── Mutual Fund Data ─────────────────────────────────────────── */
export const MUTUAL_FUNDS: MutualFund[] = [
    {
        id: 'cordros-money',
        name: 'Cordros Money Market Fund',
        manager: 'Cordros Asset Management',
        category: 'Money Market',
        risk: 'Low',
        riskColor: B.green,
        returns: { d1: 0.041, ytd: 12.4, yr1: 13.0, yr3: 11.6 },
        minInvestment: 5000,
        nav: 1.00,
        aum: '₦52B',
        color: B.red,
        description: 'A low-risk money market fund investing in treasury bills, commercial papers, bank placements, and other short-term government securities. Designed to deliver stable returns with high liquidity.',
        highlights: ['Daily liquidity', 'Short-term instruments', 'Capital preservation', 'SEC licensed'],
        chart: [100, 100.08, 100.2, 100.18, 100.35, 100.4, 100.55, 100.65, 100.72, 100.9, 100.95, 101.1, 101.2, 101.35, 101.5],
    },
    {
        id: 'stanbic-money',
        name: 'Stanbic IBTC Money Market Fund',
        manager: 'Stanbic IBTC Asset Mgmt',
        category: 'Money Market',
        risk: 'Low',
        riskColor: B.green,
        returns: { d1: 0.04, ytd: 12.8, yr1: 13.2, yr3: 11.9 },
        minInvestment: 5000,
        nav: 1.00,
        aum: '₦285B',
        color: B.green,
        description: 'Invests in short-term money market instruments including treasury bills, commercial papers, and bank deposits. Ideal for capital preservation with steady competitive returns.',
        highlights: ['Daily liquidity', 'T-Bill backed', 'No lock-in period', 'SEC licensed'],
        chart: [100, 100.1, 100.3, 100.2, 100.5, 100.4, 100.8, 101.0, 101.1, 101.4, 101.3, 101.6, 101.8, 102.0, 102.2],
    },
    {
        id: 'arm-discovery',
        name: 'ARM Discovery Balanced Fund',
        manager: 'ARM Investment Managers',
        category: 'Balanced',
        risk: 'Medium',
        riskColor: B.orange,
        returns: { d1: 0.12, ytd: 18.4, yr1: 21.3, yr3: 19.7 },
        minInvestment: 10000,
        nav: 3.47,
        aum: '₦42B',
        color: B.orange,
        description: 'A diversified balanced fund with exposure to Nigerian equities and fixed income instruments, targeting steady long-term capital appreciation.',
        highlights: ['Diversified portfolio', 'Quarterly rebalancing', 'Active management', 'SEC licensed'],
        chart: [100, 102, 101, 104, 103, 107, 106, 110, 108, 112, 111, 115, 114, 118, 117],
    },
    {
        id: 'cardinalstone-equity',
        name: 'CardinalStone Equity Fund',
        manager: 'CardinalStone Partners',
        category: 'Equity',
        risk: 'High',
        riskColor: B.red,
        returns: { d1: 0.31, ytd: 34.2, yr1: 38.6, yr3: 29.4 },
        minInvestment: 20000,
        nav: 8.92,
        aum: '₦18B',
        color: B.purple,
        description: 'Concentrated exposure to high-conviction Nigerian equities listed on the NGX. Designed for growth-oriented investors with a long investment horizon.',
        highlights: ['NGX top picks', 'High growth potential', 'Active stock selection', 'SEC licensed'],
        chart: [100, 105, 103, 110, 108, 115, 112, 120, 118, 126, 123, 131, 128, 136, 134],
    },
    {
        id: 'vetiva-money',
        name: 'Vetiva Money Market Fund',
        manager: 'Vetiva Capital Management',
        category: 'Money Market',
        risk: 'Low',
        riskColor: B.green,
        returns: { d1: 0.038, ytd: 11.9, yr1: 12.7, yr3: 11.2 },
        minInvestment: 5000,
        nav: 1.00,
        aum: '₦96B',
        color: B.blue,
        description: 'Low-risk fund investing in treasury bills, commercial papers, banker acceptances, and bank placements. Provides stable returns with high liquidity.',
        highlights: ['Same-day redemption', 'Govt-backed instruments', 'Low volatility', 'SEC licensed'],
        chart: [100, 100.1, 100.2, 100.15, 100.35, 100.3, 100.55, 100.7, 100.65, 100.9, 100.85, 101.1, 101.2, 101.35, 101.5],
    },
    {
        id: 'fbn-fixed',
        name: 'FBN Fixed Income Fund',
        manager: 'FBN Capital Asset Mgmt',
        category: 'Fixed Income',
        risk: 'Low-Medium',
        riskColor: '#34C759',
        returns: { d1: 0.08, ytd: 16.1, yr1: 17.4, yr3: 15.8 },
        minInvestment: 10000,
        nav: 2.14,
        aum: '₦61B',
        color: '#34C759',
        description: 'Invests primarily in Federal Government bonds, state bonds, and corporate fixed income securities. Provides higher returns than money market with moderate risk.',
        highlights: ['FGN bond exposure', 'Bi-annual income', 'Duration management', 'SEC licensed'],
        chart: [100, 101, 100.5, 102, 101.5, 103, 102.5, 104, 103.8, 105, 104.5, 106, 105.8, 107, 106.5],
    },
    {
        id: 'cowrywise-dollar',
        name: 'Cowrywise Dollar Fund',
        manager: 'Cowrywise Financial',
        category: 'Eurobond / Dollar',
        risk: 'Low-Medium',
        riskColor: '#34C759',
        returns: { d1: 0.005, ytd: 8.2, yr1: 9.1, yr3: 7.8 },
        minInvestment: 5000,
        nav: 1.00,
        aum: '$142M',
        color: B.gold,
        description: 'Invest in dollar-denominated Eurobonds and USD money market instruments. Protects against Naira devaluation while generating stable USD returns.',
        highlights: ['USD returns', 'FX hedge', 'Eurobond exposure', 'SEC licensed'],
        chart: [100, 100.2, 100.1, 100.4, 100.35, 100.6, 100.55, 100.8, 100.75, 101.0, 100.95, 101.2, 101.15, 101.4, 101.35],
    },
];

/* ─── NGX Stock Data ───────────────────────────────────────────── */
export const NGX_STOCKS: Stock[] = [
    {
        id: 'dangote-cement',
        ticker: 'DANGCEM',
        name: 'Dangote Cement',
        sector: 'Building Materials',
        price: 842.00,
        change: 2.4,
        changeAbs: 19.70,
        high52: 980.00,
        low52: 620.00,
        marketCap: '₦14.36T',
        peRatio: 18.4,
        dividend: 3.2,
        color: '#FF6B35',
        description: "Africa's largest cement producer. Operates in 10 African countries with over 51.6 MTPA capacity.",
        chart: [760, 772, 768, 780, 775, 788, 782, 795, 800, 810, 808, 820, 818, 832, 842],
    },
    {
        id: 'gtco',
        ticker: 'GTCO',
        name: 'Guaranty Trust Holding',
        sector: 'Banking',
        price: 58.40,
        change: -0.8,
        changeAbs: -0.47,
        high52: 72.50,
        low52: 46.20,
        marketCap: '₦1.71T',
        peRatio: 4.2,
        dividend: 8.1,
        color: B.orange,
        description: 'Leading Nigerian financial holding company offering banking, insurance, pension, and payments across Africa.',
        chart: [64, 63, 62, 61, 60.5, 61.2, 60.8, 59.9, 60.2, 59.8, 59.1, 58.9, 58.7, 58.5, 58.4],
    },
    {
        id: 'mtn-nigeria',
        ticker: 'MTNN',
        name: 'MTN Nigeria',
        sector: 'Telecoms',
        price: 312.50,
        change: 1.2,
        changeAbs: 3.70,
        high52: 385.00,
        low52: 248.00,
        marketCap: '₦6.35T',
        peRatio: 22.1,
        dividend: 1.8,
        color: '#FFCC00',
        description: "Nigeria's largest telecoms operator with 84M+ subscribers. Leading in mobile money and data services.",
        chart: [288, 292, 296, 294, 298, 296, 301, 299, 304, 302, 307, 305, 309, 308, 312.5],
    },
    {
        id: 'zenith-bank',
        ticker: 'ZENITHBA',
        name: 'Zenith Bank',
        sector: 'Banking',
        price: 47.20,
        change: 3.1,
        changeAbs: 1.42,
        high52: 58.00,
        low52: 36.80,
        marketCap: '₦1.48T',
        peRatio: 3.8,
        dividend: 9.4,
        color: '#E50914',
        description: "One of Nigeria's largest banks by assets. Strong retail and corporate banking franchise with consistent dividend history.",
        chart: [40, 41, 42, 41.5, 42.8, 43, 44.2, 43.8, 44.9, 45.4, 45.8, 46.2, 46.5, 46.9, 47.2],
    },
    {
        id: 'airtel-africa',
        ticker: 'AIRTELAFRI',
        name: 'Airtel Africa',
        sector: 'Telecoms',
        price: 2180.00,
        change: -1.4,
        changeAbs: -30.90,
        high52: 2620.00,
        low52: 1980.00,
        marketCap: '₦8.11T',
        peRatio: 15.2,
        dividend: 2.1,
        color: '#FF0000',
        description: 'Pan-African telecoms giant operating in 14 countries. Fast-growing mobile money platform across sub-Saharan Africa.',
        chart: [2300, 2280, 2260, 2250, 2240, 2230, 2220, 2215, 2210, 2205, 2195, 2190, 2188, 2185, 2180],
    },
    {
        id: 'seplat',
        ticker: 'SEPLAT',
        name: 'Seplat Energy',
        sector: 'Oil & Gas',
        price: 5640.00,
        change: 4.2,
        changeAbs: 227.50,
        high52: 6200.00,
        low52: 3840.00,
        marketCap: '₦3.32T',
        peRatio: 8.6,
        dividend: 5.2,
        color: B.green,
        description: 'Leading indigenous Nigerian oil & gas company dual-listed on NGX and LSE. Focus on onshore production in the Niger Delta.',
        chart: [4900, 4980, 5050, 5020, 5150, 5120, 5250, 5220, 5350, 5420, 5480, 5520, 5560, 5600, 5640],
    },
    {
        id: 'buacement',
        ticker: 'BUACEMENT',
        name: 'BUA Cement',
        sector: 'Building Materials',
        price: 122.00,
        change: 0.8,
        changeAbs: 0.97,
        high52: 148.00,
        low52: 98.00,
        marketCap: '₦4.39T',
        peRatio: 12.4,
        dividend: 4.1,
        color: B.purple,
        description: "Nigeria's second largest cement maker with 11MTPA capacity. Key beneficiary of infrastructure and housing boom.",
        chart: [112, 113.5, 114, 113.8, 115, 114.5, 116, 115.8, 117.5, 117, 118.5, 119, 120, 121, 122],
    },
    {
        id: 'stanbic-ibtc',
        ticker: 'STANBIC',
        name: 'Stanbic IBTC Holdings',
        sector: 'Banking',
        price: 88.50,
        change: 1.6,
        changeAbs: 1.39,
        high52: 102.00,
        low52: 68.00,
        marketCap: '₦843B',
        peRatio: 5.1,
        dividend: 6.8,
        color: B.blue,
        description: 'Diversified financial services group offering banking, brokerage, asset management, insurance, and pensions.',
        chart: [80, 81.5, 82, 81.8, 83.2, 83, 84.5, 84, 85.5, 85.2, 86.5, 86, 87.2, 88, 88.5],
    },
];

/* ─── Portfolio snapshot (demo) ───────────────────────────────── */
export const PORTFOLIO = {
    totalValue: 287450,
    totalGain:  34820,
    gainPct:    13.8,
    holdings: [
        { name: 'Stanbic Money Mkt', value: 120000, gain: 12.8, color: B.green,   type: 'fund'  },
        { name: 'CardinalStone Eq.', value: 87450,  gain: 38.6, color: B.purple,  type: 'fund'  },
        { name: 'DANGCEM',           value: 50000,  gain: 5.2,  color: '#FF6B35', type: 'stock' },
        { name: 'SEPLAT',            value: 30000,  gain: 12.4, color: B.green,   type: 'stock' },
    ],
};